import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { 
  BarChart3, Users, Globe, Smartphone, Monitor, Tablet,
  Clock, TrendingUp, Eye, Heart, ArrowLeft, Calendar,
  ChevronRight, Zap, Target, Share2
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#E63946', '#FF6B35', '#FFB703', '#2EC4B6', '#8338EC', '#3B82F6'];

function CreatorAnalyticsPage() {
  const { isAuthenticated, isCreator, token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedComic, setSelectedComic] = useState(null);

  useEffect(() => {
    if (isAuthenticated && isCreator) {
      fetchAnalytics();
    }
  }, [isAuthenticated, isCreator, token]);

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/creator/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!isCreator) {
    return (
      <div className="min-h-screen py-12 page-padding">
        <div className="max-w-2xl mx-auto text-center">
          <BarChart3 size={64} className="mx-auto text-[var(--text-secondary)] mb-6" />
          <h1 className="text-4xl font-teko mb-4">Creator Access Required</h1>
          <p className="text-[var(--text-secondary)] mb-8">
            Analytics are available for Creator and Contributor subscribers.
          </p>
          <Link to="/pricing" className="px-6 py-3 bg-[var(--primary)] text-white rounded-lg">
            Upgrade Now
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen py-12 page-padding">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-48 bg-[var(--border)] rounded" />
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={`skeleton-${i}`} className="h-32 bg-[var(--border)] rounded-xl" />
              ))}
            </div>
            <div className="h-80 bg-[var(--border)] rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  const peakHour = analytics?.peak_hour || 20;
  const peakTimeLabel = `${peakHour}:00 - ${(peakHour + 1) % 24}:00`;

  return (
    <div className="min-h-screen py-12 page-padding" data-testid="creator-analytics-page">
      <SEO title="Creator Analytics" description="Detailed insights into your comic performance, reader demographics, and engagement metrics." />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--primary)] mb-2">
              <ArrowLeft size={18} />
              Back to Dashboard
            </Link>
            <h1 className="text-4xl font-teko">Creator Analytics</h1>
            <p className="text-[var(--text-secondary)]">Deep insights into your audience and content performance</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-[var(--text-secondary)]">Last updated</p>
            <p className="font-medium">{new Date().toLocaleDateString()}</p>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <MetricCard
            title="Total Readers"
            value={analytics?.total_readers?.toLocaleString() || 0}
            icon={Users}
            color="text-blue-500"
            bgColor="bg-blue-500/10"
          />
          <MetricCard
            title="Total Views"
            value={analytics?.total_views?.toLocaleString() || 0}
            icon={Eye}
            color="text-green-500"
            bgColor="bg-green-500/10"
          />
          <MetricCard
            title="Engagement Rate"
            value={`${analytics?.engagement_rate || 0}%`}
            icon={Heart}
            color="text-[var(--primary)]"
            bgColor="bg-[var(--primary)]/10"
          />
          <MetricCard
            title="Peak Reading Time"
            value={peakTimeLabel}
            icon={Clock}
            color="text-purple-500"
            bgColor="bg-purple-500/10"
          />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Views Over Time */}
          <div className="lg:col-span-2 bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-teko">Views Over Time</h3>
                <p className="text-sm text-[var(--text-secondary)]">Last 30 days</p>
              </div>
              <TrendingUp className="text-green-500" size={20} />
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics?.growth_trend || []}>
                  <defs>
                    <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#E63946" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#E63946" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="var(--text-secondary)"
                    tick={{ fill: 'var(--text-secondary)', fontSize: 10 }}
                    interval={4}
                  />
                  <YAxis 
                    stroke="var(--text-secondary)"
                    tick={{ fill: 'var(--text-secondary)', fontSize: 10 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--surface)', 
                      border: '1px solid var(--border)',
                      borderRadius: '8px'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="views" 
                    stroke="#E63946" 
                    fillOpacity={1} 
                    fill="url(#colorViews)"
                    name="Views"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Device Distribution */}
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
            <h3 className="text-xl font-teko mb-6">Reader Devices</h3>
            <div className="h-48 mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analytics?.demographics?.devices || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={5}
                    dataKey="percentage"
                  >
                    {(analytics?.demographics?.devices || []).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value}%`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {(analytics?.demographics?.devices || []).map((device, i) => {
                const Icon = device.icon === 'smartphone' ? Smartphone : 
                            device.icon === 'monitor' ? Monitor : Tablet;
                return (
                  <div key={`device-${device.name}`} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                      <Icon size={14} className="text-[var(--text-secondary)]" />
                      <span className="text-sm">{device.name}</span>
                    </div>
                    <span className="font-medium">{device.percentage}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Peak Reading Times */}
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-teko">Peak Reading Times</h3>
                <p className="text-sm text-[var(--text-secondary)]">When your readers are most active</p>
              </div>
              <Clock className="text-purple-500" size={20} />
            </div>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics?.peak_reading_times?.filter((_, i) => i % 2 === 0) || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis 
                    dataKey="label" 
                    stroke="var(--text-secondary)"
                    tick={{ fill: 'var(--text-secondary)', fontSize: 10 }}
                  />
                  <YAxis 
                    stroke="var(--text-secondary)"
                    tick={{ fill: 'var(--text-secondary)', fontSize: 10 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--surface)', 
                      border: '1px solid var(--border)',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="reads" fill="#8338EC" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Geographic Distribution */}
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-teko">Reader Regions</h3>
                <p className="text-sm text-[var(--text-secondary)]">Where your audience is from</p>
              </div>
              <Globe className="text-blue-500" size={20} />
            </div>
            <div className="space-y-3">
              {(analytics?.demographics?.regions || []).map((region) => (
                <div key={`region-${region.name}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>{region.name}</span>
                    <span className="font-medium">{region.percentage}%</span>
                  </div>
                  <div className="h-2 bg-[var(--background)] rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${region.percentage}%`,
                        backgroundColor: COLORS[(analytics?.demographics?.regions || []).indexOf(region) % COLORS.length]
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Age Demographics */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-teko">Age Distribution</h3>
              <p className="text-sm text-[var(--text-secondary)]">Your audience demographics</p>
            </div>
            <Target className="text-[var(--primary)]" size={20} />
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics?.demographics?.age_groups || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis type="number" stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)' }} />
                <YAxis dataKey="name" type="category" stroke="var(--text-secondary)" tick={{ fill: 'var(--text-secondary)' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--surface)', 
                    border: '1px solid var(--border)',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => `${value}%`}
                />
                <Bar dataKey="percentage" fill="#E63946" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Comic Performance Comparison */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-teko">Comic Performance Comparison</h3>
              <p className="text-sm text-[var(--text-secondary)]">See how each of your comics is performing</p>
            </div>
            <Zap className="text-yellow-500" size={20} />
          </div>
          
          {analytics?.comic_comparisons?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--border)]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--text-secondary)]">Comic</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[var(--text-secondary)]">Views</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[var(--text-secondary)]">Likes</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[var(--text-secondary)]">Episodes</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[var(--text-secondary)]">Engagement</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[var(--text-secondary)]">Weekly</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.comic_comparisons.map((comic, i) => (
                    <tr key={comic.id} className="border-b border-[var(--border)] hover:bg-[var(--background)]">
                      <td className="py-3 px-4">
                        <Link to={`/comic/${comic.id}`} className="flex items-center gap-3 hover:text-[var(--primary)]">
                          <img src={comic.cover_image} alt={comic.title} className="w-10 h-14 object-cover rounded" />
                          <div>
                            <p className="font-medium line-clamp-1">{comic.title}</p>
                            <p className="text-xs text-[var(--text-secondary)]">{comic.genre}</p>
                          </div>
                        </Link>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className="font-medium">{comic.total_views.toLocaleString()}</span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className="flex items-center justify-end gap-1">
                          <Heart size={14} className="text-[var(--primary)]" />
                          {comic.total_likes.toLocaleString()}
                        </span>
                      </td>
                      <td className="text-right py-3 px-4">{comic.episode_count}</td>
                      <td className="text-right py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          comic.engagement_rate > 10 ? 'bg-green-500/20 text-green-500' :
                          comic.engagement_rate > 5 ? 'bg-yellow-500/20 text-yellow-500' :
                          'bg-[var(--background)] text-[var(--text-secondary)]'
                        }`}>
                          {comic.engagement_rate}%
                        </span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className="flex items-center justify-end gap-1 text-green-500">
                          <TrendingUp size={14} />
                          {comic.weekly_views}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-[var(--text-secondary)]">
              <p>No comics yet. Start creating to see performance data!</p>
            </div>
          )}
        </div>

        {/* Traffic Sources */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-teko">Traffic Sources</h3>
              <p className="text-sm text-[var(--text-secondary)]">How readers find your comics</p>
            </div>
            <Share2 className="text-blue-500" size={20} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(analytics?.top_referrers || []).map((ref) => (
              <div key={`referrer-${ref.source}`} className="p-4 bg-[var(--background)] rounded-xl text-center">
                <p className="text-2xl font-teko text-[var(--primary)]">{ref.percentage}%</p>
                <p className="text-sm text-[var(--text-secondary)]">{ref.source}</p>
                <p className="text-xs text-[var(--text-secondary)]">{ref.visits.toLocaleString()} visits</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Social Shares */}
        {analytics?.share_stats && (
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-teko">Social Shares</h3>
                <p className="text-sm text-[var(--text-secondary)]">How your content is being shared</p>
              </div>
              <TrendingUp className="text-green-500" size={20} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-[var(--background)] rounded-xl text-center">
                <p className="text-2xl font-teko text-blue-400">{analytics.share_stats.twitter || 0}</p>
                <p className="text-sm text-[var(--text-secondary)]">Twitter/X</p>
              </div>
              <div className="p-4 bg-[var(--background)] rounded-xl text-center">
                <p className="text-2xl font-teko text-blue-600">{analytics.share_stats.facebook || 0}</p>
                <p className="text-sm text-[var(--text-secondary)]">Facebook</p>
              </div>
              <div className="p-4 bg-[var(--background)] rounded-xl text-center">
                <p className="text-2xl font-teko text-green-500">{analytics.share_stats.whatsapp || 0}</p>
                <p className="text-sm text-[var(--text-secondary)]">WhatsApp</p>
              </div>
              <div className="p-4 bg-[var(--background)] rounded-xl text-center">
                <p className="text-2xl font-teko text-[var(--text-secondary)]">{analytics.share_stats.copy_link || 0}</p>
                <p className="text-sm text-[var(--text-secondary)]">Link Copied</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ title, value, icon: Icon, color, bgColor }) {
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5 hover:border-[var(--primary)]/30 transition-colors">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-[var(--text-secondary)] mb-1">{title}</p>
          <p className="text-2xl font-teko font-semibold">{value}</p>
        </div>
        <div className={`p-3 rounded-xl ${bgColor}`}>
          <Icon className={color} size={20} />
        </div>
      </div>
    </div>
  );
}

export default CreatorAnalyticsPage;
